import { onBeforeUnmount, ref, watch, type Ref } from "vue";

type UseElementVisibilityOptions = {
  /**
   * Margin around the root (viewport) that grows the area considered
   * "visible", so loading can start slightly before the element scrolls in.
   */
  rootMargin?: string;
  /**
   * Minimum time (ms) the element must stay intersecting before `isVisible`
   * flips to `true`. Filters out elements that only flash past during fast
   * scrolls. The flip back to `false` is always immediate. Defaults to 0.
   */
  delay?: number;
};

/**
 * Tracks whether an element is intersecting the viewport via IntersectionObserver.
 *
 * Returns a reactive boolean that flips as the element enters/leaves the
 * viewport. The observer re-attaches automatically when the target ref changes
 * and is disconnected on unmount. Falls back to always-visible when
 * IntersectionObserver is unavailable.
 */
export function useElementVisibility(
  target: Ref<Element | null>,
  options: UseElementVisibilityOptions = {},
): Ref<boolean> {
  const { rootMargin = "200px", delay = 0 } = options;
  const isVisible = ref(false);

  if (typeof IntersectionObserver === "undefined") {
    isVisible.value = true;
    return isVisible;
  }

  let observer: IntersectionObserver | null = null;
  let dwellTimer: ReturnType<typeof setTimeout> | null = null;

  function clearDwellTimer(): void {
    if (dwellTimer !== null) {
      clearTimeout(dwellTimer);
      dwellTimer = null;
    }
  }

  function disconnect(): void {
    clearDwellTimer();
    observer?.disconnect();
    observer = null;
  }

  const stopWatch = watch(
    target,
    (element) => {
      disconnect();
      if (!element) {
        isVisible.value = false;
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          const intersecting = entries.some((entry) => entry.isIntersecting);

          if (!intersecting) {
            clearDwellTimer();
            isVisible.value = false;
            return;
          }

          if (isVisible.value) return;
          if (delay <= 0) {
            isVisible.value = true;
          } else if (dwellTimer === null) {
            // Only flip to visible if it stays intersecting for `delay` ms.
            dwellTimer = setTimeout(() => {
              dwellTimer = null;
              isVisible.value = true;
            }, delay);
          }
        },
        { rootMargin },
      );
      observer.observe(element);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    stopWatch();
    disconnect();
  });

  return isVisible;
}
